import { useAppDispatch } from '../../../store/hooks'
import { useCallback } from 'react'
import {
  ErrorEventData,
  MessageStartEventData,
  type MessageStopEventData,
  PreJsonTextDeltaData,
  StreamingEvent,
  TextDeltaEventData,
  ToolUse,
  type ToolUseData,
  type ToolUseDeltaEventData,
  ToolUseStartEventData,
  ToolUseStopEventData,
} from '../../../models/StreamingAIAgent'
import { parseEventData } from '../../../common/streamingUtils'
import { StreamingEventType } from '../../../apps/professional-app/fa/models'
import {
  addToolUseInputsToStreamingMessage,
  addToolUseToStreamingMessage,
  beginStreamingMessage,
  clearStreamingMessageIndex,
  setStreamingError,
  stopToolUseInStreamingMessage,
  updateStreamingMessage,
} from '../../../store/streamingChat/streamingChat'

export interface UseStreamingChatProps<T> {
  onTextDelta: (data: T) => void
  onNewAiAgentId: (newAiAgentId: string) => void
  onToolUseDelta?: (toolUse: ToolUseData) => void
  onMessageStop?: (toolUses: ToolUseData[]) => void
}

export const useStreamingChat = <T>({ onTextDelta, onNewAiAgentId, onToolUseDelta, onMessageStop }: UseStreamingChatProps<T>) => {
  const dispatch = useAppDispatch()

  const onStreamingEvent = useCallback(
    async (streamingEvent: StreamingEvent) => {
      const { data: eventData } = streamingEvent

      const parsedEventData = parseEventData(eventData)
      switch (streamingEvent.event) {
        case StreamingEventType.MESSAGE_START: {
          const { newAiAgentId } = parsedEventData as MessageStartEventData
          if (newAiAgentId) {
            onNewAiAgentId(newAiAgentId)
          }
          dispatch(beginStreamingMessage())
          break
        }

        case StreamingEventType.PRE_JSON_TEXT_DELTA: {
          const { data } = parsedEventData as PreJsonTextDeltaData
          if (!data) {
            return
          }
          dispatch(updateStreamingMessage(data))
          break
        }

        case StreamingEventType.TEXT_DELTA: {
          const { data } = parsedEventData as TextDeltaEventData<T>
          if (!data) {
            return
          }
          onTextDelta(data)
          break
        }

        case StreamingEventType.TOOL_USE_START: {
          const { toolUse: toolUseData } = parsedEventData as ToolUseStartEventData
          if (!toolUseData) {
            return
          }
          const toolUse: ToolUse = { ...toolUseData, status: 'running' }
          dispatch(addToolUseToStreamingMessage(toolUse))
          break
        }

        case StreamingEventType.TOOL_USE_DELTA: {
          const { toolUse } = parsedEventData as ToolUseDeltaEventData
          if (!toolUse) {
            return
          }

          if (onToolUseDelta) {
            onToolUseDelta(toolUse)
          }
          break
        }

        case StreamingEventType.TOOL_USE_STOP: {
          const { toolUse } = parsedEventData as ToolUseStopEventData
          if (!toolUse) {
            return
          }
          dispatch(stopToolUseInStreamingMessage(toolUse))
          break
        }

        case StreamingEventType.MESSAGE_STOP: {
          const { toolUses } = parsedEventData as MessageStopEventData
          if (toolUses?.length) {
            dispatch(addToolUseInputsToStreamingMessage(toolUses))
          }

          if (toolUses?.length && onMessageStop) {
            onMessageStop(toolUses)
          }

          dispatch(clearStreamingMessageIndex())
          break
        }

        case StreamingEventType.ERROR: {
          const errorEventData = parsedEventData as ErrorEventData
          dispatch(setStreamingError(errorEventData))
        }
      }
    },
    [dispatch, onMessageStop, onNewAiAgentId, onTextDelta, onToolUseDelta],
  )

  return { onStreamingEvent }
}
